import { getICIDAtomized, getICIDStr } from '@tv2/lib-tracking/icid';
import { useEffect, useRef, useState } from 'react';
import { AdobeDataType, adobeTracking } from '@tv2/lib-offspring/libs/track';
import { useGlobalTracking } from '@tv2/lib-offspring/hooks/tracking/useGlobalTracking';
import { useUser } from '@tv2/lib-offspring/api/user/UserProvider';
import { mapUrlPathToOvpPath } from '@tv2/lib-offspring/libs/ovpPath';
import { useLeanProfile } from '@tv2/lib-offspring/api/user/LeanProfileProvider';
import { useCustomerType } from '@tv2/lib-offspring/api/user/CustomerType';
import { usePrimarySubscription } from '@tv2/lib-offspring/api/user/Subscription';
import { appendToTracking } from './useAppendToTracking';

export function usePageTrack(
  data?: AdobeDataType,
  trackChanges: boolean = false,
  ready: boolean = true,
  overrides: {
    path?: string;
    pageTitle?: string;
    featureFlags?: Map<string, string>;
  } = {}
) {
  const hasTracked = useRef<any>(false);
  const [globalTracking, setGlobalTracking] = useGlobalTracking();
  const userTracking = useUserTracking();
  const user = useUser();
  const isLoggedIn = Boolean(user.user);

  const [_, setRerender] = useState(0);

  useEffect(() => {
    if (!isLoggedIn) return;
    const interval = setInterval(() => {
      setRerender(value => value + 1);
      if (appendToTracking.anti_sharing_ready) {
        clearInterval(interval);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [isLoggedIn]);

  useEffect(() => {
    if (
      !userTracking ||
      hasTracked.current === true ||
      hasTracked.current === data ||
      !ready ||
      (!appendToTracking.anti_sharing_ready && isLoggedIn)
    ) {
      return;
    }

    const { pathname } = window.location;

    const icid =
      globalTracking.current.status === 'ready'
        ? {
            icid: getICIDStr(globalTracking.current.icid),
            internalCampaign: getICIDAtomized(globalTracking.current.icid).internalCampaign,
          }
        : undefined;

    const breadcrumbs = data?.pageName
      ? data.pageName.split('>')
      : [
          'tv 2 play',
          ...(overrides?.path ?? mapUrlPathToOvpPath(pathname))
            .replace(/-([0-9a-f-]+){5}$/i, '')
            .replace('/c/', '/focuspage/')
            .split('/')
            .filter(Boolean),
        ];

    if (breadcrumbs.length === 3 && breadcrumbs[1] === 'vis') {
      // hack hack hack
      breadcrumbs[1] = 'showall';
      breadcrumbs[2] = overrides.pageTitle ?? breadcrumbs[2];
    }

    hasTracked.current = trackChanges ? data : true;
    setGlobalTracking({ status: 'missing' });

    adobeTracking({
      event: 'page view',
      data: {
        language: 'da-dk',
        pageType: 'Content',
        breadcrumbs,
        featureFlags: overrides.featureFlags,
        ...userTracking,
        ...appendToTracking.anti_sharing,
        ...icid,
        ...data,
      },
    });
  }, [
    globalTracking.current,
    data,
    globalTracking.current.status,
    overrides.pageTitle,
    overrides,
    overrides?.path,
    ready,
    setGlobalTracking,
    data?.pageName,
    data?.featureFlags,
    trackChanges,
    userTracking,
    isLoggedIn,
  ]);
}

function useUserTracking() {
  const user = useUser();
  const {
    profiles,
    profile: { completed, profile },
  } = useLeanProfile();
  const customerType = useCustomerType();
  const primary = usePrimarySubscription();

  function profileComposition() {
    const childProfiles = profiles.filter(p => p.kidsProfile).length;
    const adultProfiles = profiles.length - childProfiles;
    // r = restricted, n = non-restricted, t = total
    return `r${childProfiles}_n${adultProfiles}_t${profiles.length}`;
  }

  const ready = user.completed && !primary.loading && !customerType.loading && completed;

  if (!ready) return undefined;

  const merged = mergeSubscriptions(customerType.subscriptions, primary.subscription);
  const customerStatus = user.user ? 'logged in' : 'not logged in';

  return {
    customerStatus,
    loginStatus: customerStatus,
    customerId: user.user?.userId,
    traceId: user.user?.adobeTraceId,
    subscriptions: merged,
    subscription: customerType.subscriptionTracking,

    ...(profile
      ? {
          profile: {
            profileTraceId: `${user.user?.adobeTraceId}_${profile?.id}`,
            profileId: profile?.id,
            profileType: profile?.kidsProfile ? 'restricted' : 'non-restricted',
            profileInfo: profileComposition(),
          },
        }
      : {}),
  };
}

function mergeSubscriptions(
  customerType: ReturnType<typeof useCustomerType>['subscriptions'],
  subscription: ReturnType<typeof usePrimarySubscription>['subscription']
) {
  return customerType?.map(s => {
    if (subscription) {
      const { addons, alert, ...rest } = subscription ?? {};
      const isPrimary = rest?.product?.id === s.id;
      if (isPrimary) {
        return {
          ...s,
          ...rest,
        };
      }
      const addon = addons?.find(a => a.product?.id === s.id);
      if (addon) {
        return {
          ...s,
          ...addon,
        };
      }
    }
    return {
      ...s,
      internal: s.providerType === 'DTC',
      product: {
        id: s.id,
      },
    };
  });
}
